import {
  Box,
  Checkbox,
  CircularProgress,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import { Dispatch } from '@reduxjs/toolkit';
import CustomPagination from 'components/CustomPagination/CustomPagination';
import { OrderEnum } from 'enums/orderEnum';
import { SearchByEnum } from 'enums/searchByEnum';
import Refresh from 'icons/Refresh';
import moment from 'moment';
import React, { ChangeEvent, Component, Fragment } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AppDispatch, AppState } from 'store';
import {
  getContent,
  getContentTypes,
  getTaxonomy,
  getWorkflowSteps,
  setPagination,
  toggleAllEntries,
  toggleEntry,
} from 'store/content/content.actions';
import { IContentPagination, IContentState } from 'store/content/content.interface';
import { getContentSelector } from 'store/content/content.selectors';
import { getContentSpinnerSelector } from 'store/spinner/spinner.selectors';
import { RefreshButton, Spacer, TableToolbarDiv } from 'styles/shared.styled';
import { debounce } from 'utils/debounce';
import { getWorkflowStepColor } from 'utils/getWorkflowStepColor';
import { KontentProductDTO } from 'utils/restApplicationClient';
import AddManyContentSearch from '../AddManyContentSearch/AddManyContentSearch';
import AddManyContentsFilter from '../Filter/AddManyContentsFilter';
import {
  AddManyContentsTableHeaderTableCell,
  AddManyContentsTableMainContainer,
  AddManyContentsTableSpinner,
  AddManyContentsTableTable,
  AddManyContentsTableTableContainer,
  AddManyContentsTableWorkflowStep,
} from './AddManyContentsTable.styled';

interface IState {
  searchSpinner: boolean;
  searchValue: string;
  searchBy: SearchByEnum;
}

interface IStateProps {
  contentState: IContentState;
  contentSpinner: boolean;
}

interface IDispatchProps {
  getContent: (payload: IContentPagination) => AppDispatch;
  setPagination: (payload: IContentPagination) => AppDispatch;
  toggleEntry: (payload: KontentProductDTO) => AppDispatch;
  toggleAllEntries: () => AppDispatch;
  getContentTypes: () => AppDispatch;
  getWorkflowSteps: () => AppDispatch;
  getTaxonomies: () => AppDispatch;
}

type PropType = WithTranslation & IDispatchProps & IStateProps;

export class AddManyContentsTable extends Component<PropType, IState> {
  constructor(props: PropType) {
    super(props);

    const {
      contentState: {
        pagination: { search, searchBy },
      },
    } = this.props;

    this.state = {
      searchSpinner: false,
      searchValue: search || '',
      searchBy: searchBy,
    };
  }

  componentDidMount(): void {
    const {
      getContent,
      getContentTypes,
      getWorkflowSteps,
      getTaxonomies,
      contentState: { pagination },
    } = this.props;

    getContent(pagination);
    getContentTypes();
    getWorkflowSteps();
    getTaxonomies();
  }

  private debounceFn?: Function;

  onSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const {
      contentState: { pagination },
      setPagination,
    } = this.props;

    this.setState({
      searchSpinner: true,
      searchValue: event.target.value,
    });
    event.persist();

    if (!this.debounceFn) {
      this.debounceFn = debounce(() => {
        this.setState(
          {
            searchSpinner: false,
          },
          () => {
            const { searchValue, searchBy } = this.state;

            if (!searchValue || searchValue.length >= 3) {
              setPagination({
                ...pagination,
                page: 0,
                search: searchValue,
                searchBy: searchBy,
              });
            }
          },
        );
      }, 500);
    }
    this.debounceFn();
  };

  onSearchByChange = (newSearchByValue: SearchByEnum) => (): void => {
    const {
      contentState: { pagination },
      setPagination,
    } = this.props;

    const { searchValue, searchBy } = this.state;

    if (searchBy !== newSearchByValue) {
      this.setState({
        searchBy: newSearchByValue,
      });

      if (searchValue && searchValue.length >= 3) {
        setPagination({
          ...pagination,
          page: 0,
          searchBy: newSearchByValue,
        });
      }
    }
  };

  handleRefresh = (): void => {
    const {
      contentState: { pagination },
      getContent,
    } = this.props;

    getContent(pagination);
  };

  handleChangeOrder = (header: string): (() => void) => {
    return (): void => {
      const {
        contentState: { pagination },
        setPagination,
      } = this.props;
      const { orderBy, order } = pagination;

      const isAsc = orderBy === header && order === OrderEnum.ASC;

      setPagination({
        ...pagination,
        order: isAsc ? OrderEnum.DESC : OrderEnum.ASC,
        orderBy: header,
      });
    };
  };

  checkAllEntries = () => (): void => {
    const { toggleAllEntries } = this.props;

    toggleAllEntries();
  };

  handleCheckEntry = (entry: KontentProductDTO) => (): void => {
    const { toggleEntry } = this.props;

    toggleEntry(entry);
  };

  handleChangePage = (page: number): void => {
    const {
      contentState: { pagination },
      setPagination,
    } = this.props;

    setPagination({ ...pagination, page });
  };

  handleChangeRowsPerPage = (size: number): void => {
    const {
      contentState: { pagination },
      setPagination,
    } = this.props;

    setPagination({
      ...pagination,
      size,
      page: 0,
    });
  };

  renderHeaders(): JSX.Element {
    const {
      t,
      contentState: {
        data,
        selected,
        pagination: { orderBy, order },
      },
    } = this.props;

    const headers = [
      { id: 'system.name', sortable: true },
      { id: 'system.codename', sortable: true },
      { id: 'system.workflow_step', sortable: true },
      { id: 'system.type', sortable: true },
      { id: 'system.last_modified', sortable: true },
    ];

    return (
      <TableHead>
        <TableRow>
          <AddManyContentsTableHeaderTableCell padding="checkbox">
            <Checkbox
              checked={!!(selected?.length && data?.length === selected?.length)}
              indeterminate={!!(selected?.length && data?.length && selected.length < data.length)}
              onChange={this.checkAllEntries()}
            />
          </AddManyContentsTableHeaderTableCell>
          {headers.map((header, index) => {
            return header.sortable ? (
              <AddManyContentsTableHeaderTableCell key={index} align="left">
                <TableSortLabel
                  onClick={this.handleChangeOrder(header.id)}
                  active={orderBy === header.id}
                  direction={orderBy === header.id ? order : OrderEnum.ASC}
                  IconComponent={ArrowDropDown}
                >
                  <Typography component="div">
                    <Box fontSize="12px" fontWeight="400" color="#6F8294">
                      {t(`addcontent.headers.${header.id}`)}
                    </Box>
                  </Typography>
                </TableSortLabel>
              </AddManyContentsTableHeaderTableCell>
            ) : (
              <AddManyContentsTableHeaderTableCell key={index} align="left">
                <TableHead component="div">
                  <Typography component="div">
                    <Box fontSize="12px" fontWeight="400" color="#6F8294">
                      {t(`addcontent.headers.${header.id}`)}
                    </Box>
                  </Typography>
                </TableHead>
              </AddManyContentsTableHeaderTableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  renderRow(entry: KontentProductDTO): JSX.Element {
    const {
      contentState: { selected },
    } = this.props;

    return (
      <TableRow key={entry.id}>
        <TableCell padding="checkbox">
          <Checkbox
            checked={!!selected?.filter((element) => element.id === entry.id).length}
            onChange={this.handleCheckEntry(entry)}
          />
        </TableCell>
        <TableCell>
          <Tooltip title={entry.name || ''} arrow>
            <Box
              fontSize="12px"
              fontWeight="400"
              color="#333"
              maxWidth="10vw"
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
            >
              {entry.name}
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tooltip title={entry.codeName || ''} arrow>
            <Box
              fontSize="12px"
              fontWeight="400"
              color="#333"
              maxWidth="10vw"
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
            >
              {entry.codeName}
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell>
          <AddManyContentsTableWorkflowStep background={getWorkflowStepColor(entry.workflowStep)}>
            <Box fontSize="12px" fontWeight="400" color={entry.workflowStep === 'review' ? '#201B00' : undefined}>
              {entry.workflowStep}
            </Box>
          </AddManyContentsTableWorkflowStep>
        </TableCell>
        <TableCell>
          <Tooltip title={entry.typeName || ''} arrow>
            <Box
              fontSize="12px"
              fontWeight="400"
              color="#333"
              maxWidth="7vw"
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
            >
              {entry.typeName}
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Box fontSize="12px" fontWeight="400" color="#333">
            {entry?.lastModification ? moment(entry.lastModification).format('DD-MM-YYYY') : '-'}
          </Box>
        </TableCell>
      </TableRow>
    );
  }

  render(): JSX.Element {
    const {
      t,
      contentSpinner,
      contentState: { data, pagination, totalPages },
    } = this.props;
    const { searchSpinner, searchValue, searchBy } = this.state;

    return (
      <Fragment>
        <TableToolbarDiv>
          <AddManyContentsFilter />
          <RefreshButton startIcon={<Refresh />} onClick={this.handleRefresh}>
            <Box fontSize="14px" fontWeight="500" color="#555">
              {t('addcontent.refresh')}
            </Box>
          </RefreshButton>
          <Spacer />
          <AddManyContentSearch
            onChange={this.onSearchChange}
            onSearchByChange={this.onSearchByChange}
            spinner={searchSpinner}
            value={searchValue}
            searchBy={searchBy}
          />
        </TableToolbarDiv>
        <AddManyContentsTableMainContainer>
          <AddManyContentsTableTableContainer>
            <AddManyContentsTableTable stickyHeader size="small">
              {this.renderHeaders()}
              <TableBody>
                {contentSpinner ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <AddManyContentsTableSpinner>
                        <CircularProgress size={80} color="secondary" />
                      </AddManyContentsTableSpinner>
                    </TableCell>
                  </TableRow>
                ) : data && data?.length ? (
                  data.map((entry) => this.renderRow(entry))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography align="center" variant="body1" color="textSecondary">
                        {t('addcontent.emptyEntriesTable')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </AddManyContentsTableTable>
          </AddManyContentsTableTableContainer>
          <CustomPagination
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            defaultSize={pagination?.size || 10}
            page={pagination?.page || 0}
            totalPages={totalPages || 0}
            onPageChange={this.handleChangePage}
            onRowChange={this.handleChangeRowsPerPage}
          ></CustomPagination>
        </AddManyContentsTableMainContainer>
      </Fragment>
    );
  }
}

const mapStateToProps = (state: AppState): IStateProps => ({
  contentState: getContentSelector(state),
  contentSpinner: getContentSpinnerSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  getContent: (payload: IContentPagination): AppDispatch => dispatch(getContent(payload)),
  setPagination: (payload: IContentPagination): AppDispatch => dispatch(setPagination(payload)),
  toggleEntry: (payload: KontentProductDTO): AppDispatch => dispatch(toggleEntry(payload)),
  toggleAllEntries: (): AppDispatch => dispatch(toggleAllEntries()),
  getContentTypes: (): AppDispatch => dispatch(getContentTypes()),
  getWorkflowSteps: (): AppDispatch => dispatch(getWorkflowSteps()),
  getTaxonomies: (): AppDispatch => dispatch(getTaxonomy()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(AddManyContentsTable));
